import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, onSessionChange, refreshSession, setAccessToken } from './api';
import type { LoginResponse, MeDto, TokenResponse } from '../../shared/api';
import type { Permission } from '../../shared/permissions';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: MeDto | null;
  can: (...perms: Permission[]) => boolean;
  canAny: (...perms: Permission[]) => boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verifyTwoFactor: (challenge: string, code: string) => Promise<void>;
  acceptSession: (session: TokenResponse) => void;
  logout: () => Promise<void>;
  setUser: (user: MeDto) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUserState] = useState<MeDto | null>(null);
  const queryClient = useQueryClient();

  const acceptSession = useCallback((session: TokenResponse) => {
    setAccessToken(session.accessToken);
    setUserState(session.user);
    setStatus('authenticated');
  }, []);

  useEffect(() => {
    const off = onSessionChange((session) => {
      if (session) {
        setUserState(session.user);
        setStatus('authenticated');
      } else {
        setUserState(null);
        setStatus('anonymous');
      }
    });
    void refreshSession();
    return () => {
      off();
    };
  }, []);

  // Refresh the access token shortly before it expires.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const timer = setInterval(() => void refreshSession(), 10 * 60_000);
    return () => clearInterval(timer);
  }, [status]);

  const value = useMemo<AuthState>(() => {
    const perms = new Set(user?.permissions ?? []);
    return {
      status,
      user,
      can: (...p) => p.every((x) => perms.has(x)),
      canAny: (...p) => p.some((x) => perms.has(x)),
      async login(email, password) {
        const res = await api.post<LoginResponse>('/auth/login', { email, password });
        if (res.accessToken && res.user) acceptSession({ accessToken: res.accessToken, expiresIn: res.expiresIn ?? 900, user: res.user });
        return res;
      },
      async verifyTwoFactor(challenge, code) {
        acceptSession(await api.post<TokenResponse>('/auth/login/2fa', { challenge, code }));
      },
      acceptSession,
      async logout() {
        try {
          await api.post('/auth/logout');
        } catch {
          /* ignore */
        }
        setAccessToken(null);
        setUserState(null);
        setStatus('anonymous');
        queryClient.clear();
      },
      setUser: (u) => setUserState(u),
    };
  }, [status, user, acceptSession, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
